/**
 * Strategy questionnaire review — per-stage summary cards and edit entry points.
 *
 * Belongs here: read-only review UI, out-of-sync autosubmit, Find-my-strategy click orchestration.
 * Does not belong here: session answers/leave, or risk ready UI.
 */
import { useEffect, useRef, useState } from 'react'
import type { StrategyQuestionnaireStage } from '../../shared/types/questionnaire.types'
import { getStageSummaryLines } from '../setup/config'
import { getStageIconSvg } from '../setup/stageAssets'
import { getBottomPadding } from '../../../../../utils/sessionStorage'
import { EventNames } from '../../../../../types/misc.types'
import { expertReviewSafeAreaTopStyle } from '../../shared/expertReviewSafeArea'
import { useStrategyQuestionnaireContext } from '../flow-engine/StrategyQuestionnaireProvider'
import {
  StrategyResolveResult,
  useStrategyResolve
} from '../flow-engine/useStrategyResolve'
import RenderHTML from '../../../../../components/ui-components/RenderHTML'
import { ConnectedSummaryLinesV2 } from '../../../components/ConnectedSummaryLinesV2'
import { PrimaryButton } from '../../../../../components/execution/PrimaryButton'
import { SecondaryButton } from '../../../../../components/execution/SecondaryButton'
import { useExpertReviewNavigation } from '../../navigation/expertReviewNavigation'
import { EXPERT_REVIEW_CAME_FROM } from '../../navigation/expertReviewNavigation'
import { ExpertReviewTopBar } from '../../shared/components/ExpertReviewTopBar'
import { ExpertReviewEventIds, ExpertReviewScreen } from '../../analytics/expertReviewEvents'
import {
  DirectionalSlideSwap,
  SlideDirection
} from '../../shared/transitions/DirectionalSlideSwap'
import { lightImpactVibrate, trackEvent } from '../../../../../utils/appBridge'
import {
  StrategyResolveOverlay,
  StrategyResolvePhase
} from './StrategyResolveOverlay'

export default function StrategyQuestionnaireReviewPage() {
  const navigation = useExpertReviewNavigation()
  const {
    rawQuestionnaireData,
    committedAnswers,
    committedAnswersVersion,
    isAnswersVersionOutOfSync,
    submitAnswers,
    submitAnswersAndStatusRefresh,
    beginEditOne,
    beginEditAll,
    isQuestionnaireLoading,
    riskProfile,
    markReachedReview,
    setRiskProfileFromStatus
  } = useStrategyQuestionnaireContext()

  const { resolveStrategy, retryComputeRiskProfile } = useStrategyResolve({
    questionnaireId: rawQuestionnaireData?.questionnaireId ?? '',
    committedAnswers,
    committedAnswersVersion,
    isAnswersVersionOutOfSync,
    submitAnswers,
    setRiskProfile: setRiskProfileFromStatus
  })

  // Local phase only — not a route; Idle so back from risk never replays loader
  const [resolvePhase, setResolvePhase] = useState(StrategyResolvePhase.Idle)
  // Bumps on dismiss / unmount so in-flight resolve/retry results are ignored
  const resolveGenerationRef = useRef(0)

  useEffect(() => {
    return () => {
      resolveGenerationRef.current += 1
    }
  }, [])

  // Any entry to full strategy review — hub can resume here later
  useEffect(() => {
    markReachedReview()
  }, [markReachedReview])

  // When answers version is out of sync — silent POST + status (first land + edit→back without remount)
  useEffect(() => {
    const questionnaireId = rawQuestionnaireData?.questionnaireId ?? ''
    if (!questionnaireId || Object.keys(committedAnswers).length === 0) return
    if (!isAnswersVersionOutOfSync) return
    void submitAnswersAndStatusRefresh(
      {
        questionnaireId,
        answers: committedAnswers,
        answersVersion: committedAnswersVersion
      },
      { silent: true }
    ).catch(() => {
      // Stay out of sync — Find my strategy still POSTs when needed
    })
  }, [
    committedAnswersVersion,
    committedAnswers,
    isAnswersVersionOutOfSync,
    rawQuestionnaireData?.questionnaireId,
    submitAnswersAndStatusRefresh
  ])

  const cameFrom = navigation.navigationState.cameFrom
  const openedFromHub = cameFrom === EXPERT_REVIEW_CAME_FROM.HUB
  const openedFromRiskProfile = cameFrom === EXPERT_REVIEW_CAME_FROM.RISK_PROFILE
  // Hub / risk revisit: back + menu. After questionnaire: menu only (menu without back is ok).
  const showBack = openedFromHub || openedFromRiskProfile
  // From risk profile with unchanged answers: no CTA (back only). After edits, riskProfile is cleared → show recompute.
  const showPrimaryCta = !openedFromRiskProfile || riskProfile == null
  const primaryCtaLabel = 'Find my strategy'
  const stages: StrategyQuestionnaireStage[] = rawQuestionnaireData?.stages ?? []
  const isReviewContentReady = !isQuestionnaireLoading && stages.length > 0
  const hasTrackedReviewLoad = useRef(false)

  useEffect(() => {
    if (!isReviewContentReady || hasTrackedReviewLoad.current) return
    hasTrackedReviewLoad.current = true
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: { ScreenName: ExpertReviewScreen.StrategyReview }
    })
  }, [isReviewContentReady])

  /* Wait for questionnaire to load before deciding to redirect; then redirect only if no stages. */
  if (isQuestionnaireLoading) return null
  if (!stages.length) {
    navigation.goToStrategyQuestionnaire({
      replace: true,
      cameFrom: EXPERT_REVIEW_CAME_FROM.STRATEGY_REVIEW
    })
    return null
  }

  function handleEditStage(stageIndex: number) {
    const stageId = stages[stageIndex]?.id
    if (!stageId) return
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: ExpertReviewScreen.StrategyReview,
        eventId: ExpertReviewEventIds.StrategyReviewEdit,
        buttonLabel: stages[stageIndex]?.displayText ?? 'Edit'
      }
    })
    beginEditOne(stageId)
  }

  /** Find my strategy — start work on click (not on overlay mount). */
  async function handleFindMyStrategy() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: ExpertReviewScreen.StrategyReview,
        eventId: ExpertReviewEventIds.FindMyStrategy,
        buttonLabel: primaryCtaLabel
      }
    })
    const generation = ++resolveGenerationRef.current
    setResolvePhase(StrategyResolvePhase.Loading)
    const result = await resolveStrategy()
    if (generation !== resolveGenerationRef.current) return
    if (result === StrategyResolveResult.SubmitFailed) {
      setResolvePhase(StrategyResolvePhase.Idle)
      return
    }
    if (result === StrategyResolveResult.ComputeFailed) {
      setResolvePhase(StrategyResolvePhase.ComputeError)
      return
    }
    // Push first — avoid flashing review under the loader
    navigation.goToRiskProfile({ cameFrom: EXPERT_REVIEW_CAME_FROM.STRATEGY_REVIEW })
    setResolvePhase(StrategyResolvePhase.Idle)
  }

  async function handleRetryCompute() {
    const generation = ++resolveGenerationRef.current
    setResolvePhase(StrategyResolvePhase.Loading)
    const result = await retryComputeRiskProfile()
    if (generation !== resolveGenerationRef.current) return
    if (result === StrategyResolveResult.Success) {
      navigation.goToRiskProfile({ cameFrom: EXPERT_REVIEW_CAME_FROM.STRATEGY_REVIEW })
      setResolvePhase(StrategyResolvePhase.Idle)
      return
    }
    setResolvePhase(StrategyResolvePhase.ComputeError)
  }

  function handleDismissResolveError() {
    resolveGenerationRef.current += 1
    setResolvePhase(StrategyResolvePhase.Idle)
  }

  function handleEditAllInputs() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: ExpertReviewScreen.StrategyReview,
        eventId: ExpertReviewEventIds.StrategyReviewEditAll,
        buttonLabel: 'Edit all inputs'
      }
    })
    beginEditAll()
  }

  if (resolvePhase !== StrategyResolvePhase.Idle) {
    return (
      <StrategyResolveOverlay
        phase={resolvePhase}
        onRetryCompute={() => {
          void handleRetryCompute()
        }}
        onDismissError={handleDismissResolveError}
      />
    )
  }

  return (
    <div
      className="h-screen flex flex-col min-h-0 bg-white"
      style={expertReviewSafeAreaTopStyle()}
    >
      <div className="shrink-0 px-5">
        <ExpertReviewTopBar
          onBack={showBack ? navigation.goBackFromStrategyReview : undefined}
          menu="strategy"
          analyticsScreen={ExpertReviewScreen.StrategyReview}
          testLocatorBack="strategy-review-back"
          testLocatorMenu="strategy-review-menu"
        />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-5 pt-4 pb-8">
        <DirectionalSlideSwap contentKey="strategy-review" direction={SlideDirection.Forward}>
          <h1 className="text-[22px] font-[500] text-[#232323] leading-[130%]" test-locator="strategy-review-title">
            Review your inputs
          </h1>
          <p className="pt-1 text-[13px] font-[400] text-[#6A6E6E]">
            These details will shape your ideal allocation strategy. Take a moment to see if everything looks correct.
          </p>

          <div className="mt-6 border border-[#F0F4F7] rounded-[16px]">
            {stages.map((stage, index) => (
              <div key={stage?.id ?? `stage-${index}`} className='flex flex-col'>
                <StageReviewCard
                  stage={stage}
                  summaryLines={getStageSummaryLines(stage, committedAnswers, rawQuestionnaireData?.reviewSummary)}
                  onEdit={() => handleEditStage(index)}
                  icon={<RenderHTML innerHTMLText={getStageIconSvg(stage?.id, 'review')} />}
                  testLocator={`strategy-review-stage-${stage?.id ?? index}`}
                  testLocatorEdit={`strategy-review-stage-${stage?.id ?? index}-edit`}
                />
                {index !== stages.length - 1 && <div className='h-[1px] bg-[#F0F4F7]'></div>}
              </div>
            ))}
          </div>

          <div className='pt-3'>
            <SecondaryButton testLocator="strategy-review-edit-all" onClick={handleEditAllInputs}>
              Edit all inputs
            </SecondaryButton>
          </div>
        </DirectionalSlideSwap>
      </div>

      {showPrimaryCta ? (
        <div style={{ paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
          className="shrink-0 px-5 pt-4 border-t border-[#F0F4F7]">
          <PrimaryButton
            testLocator="strategy-review-continue"
            onClick={() => {
              void handleFindMyStrategy()
            }}
            label={primaryCtaLabel}
            primaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[16px]"
          />
        </div>
      ) : null}
    </div>
  )
}

export function StageReviewCard({
  stage,
  summaryLines,
  onEdit,
  icon,
  testLocator,
  testLocatorEdit
}: {
  stage: StrategyQuestionnaireStage
  summaryLines: string[]
  onEdit: () => void
  icon: React.ReactNode
  testLocator: string
  testLocatorEdit: string
}) {
  return (
    <div className="bg-white pt-5 px-5 pb-7 rounded-[20px]">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex-shrink-0" aria-hidden>
            {icon}
          </span>
          <h3 className="truncate text-[18px] font-[400] text-[#232323]" test-locator={testLocator}>
            {stage?.displayText ?? 'Stage'}
          </h3>
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="w-[78px] h-[34px] flex items-center justify-center rounded-[12px] bg-[#F0F4F7] text-[#232323] text-[14px] font-[500]"
          test-locator={testLocatorEdit}
        >
          Edit
        </button>
      </div>
      <div className="pt-2 pl-2">
        <ConnectedSummaryLinesV2 lines={summaryLines} connectorColor='#F0F4F7' />
      </div>
    </div >
  )
}
